import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AlertTriangle, Ban, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import Modal from '../Modal';
import { useConfirmLock } from '../../hooks/useConfirmLock';
import { InlineError, Spinner, apiErrorMessage } from '../sales/shared';

const MIN_REASON = 5;

/**
 * Exceptional, irreversible hard delete of a customer (General Manager only,
 * decision 5). It is deliberately heavy: the dependency impact is loaded from
 * the server first, the server's blocking reasons disable the action, and the
 * user must give a reason and type the exact customer name. Archiving is the
 * normal way to retire a customer.
 */
export default function HardDeleteCustomerModal({
  orgId,
  orgName,
  onClose,
  onDeleted,
}: {
  orgId: string;
  orgName: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');

  const impactQuery = useQuery({
    queryKey: ['organization-delete-impact', orgId],
    queryFn: () => api.getOrganizationDeleteImpact(orgId),
    retry: false,
  });
  const impact = impactQuery.data?.data;

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteOrganization(orgId, { reason: reason.trim(), confirm_name: confirmName.trim() }),
    onSuccess: () => onDeleted(),
  });

  const confirmOnce = useConfirmLock(deleteMutation.isPending); // DQ-126: lock at the click
  const expectedName = (impact?.organization.name ?? orgName).trim();
  const nameMatches = confirmName.trim() === expectedName;
  const reasonOk = reason.trim().length >= MIN_REASON;
  const blocking = impact?.blocking ?? [];
  const nonZero = Object.entries(impact?.counts ?? {}).filter(([, n]) => Number(n) > 0);
  const canDelete = !!impact && blocking.length === 0 && nameMatches && reasonOk && !deleteMutation.isPending;

  const countLabel = (key: string) => t(`organization.deleteImpact.${key}`, key);

  return (
    <Modal open title={t('organization.hardDelete.title')} onClose={onClose} sizeClass="max-w-lg"
      icon={<Trash2 size={18} className="text-danger-600" />}>
      <div className="space-y-4 max-h-[75vh] overflow-y-auto">
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-3 flex gap-3">
          <AlertTriangle size={20} className="text-danger-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-danger-800 space-y-1">
            <p className="font-semibold">{t('organization.hardDelete.irreversible')}</p>
            <p>{t('organization.hardDelete.preferArchive')}</p>
          </div>
        </div>

        {impactQuery.isLoading ? (
          <Spinner />
        ) : impactQuery.isError ? (
          <InlineError>{apiErrorMessage(impactQuery.error, t('organization.hardDelete.impactError'))}</InlineError>
        ) : impact && (
          <>
            <div>
              <h3 className="text-sm font-semibold text-surface-800 mb-2">{t('organization.hardDelete.impactTitle')}</h3>
              {nonZero.length === 0 ? (
                <p className="text-sm text-surface-500">{t('organization.hardDelete.noDependencies')}</p>
              ) : (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {nonZero.map(([key, n]) => (
                    <li key={key} className="flex justify-between gap-2 border-b border-surface-100 py-1">
                      <span className="text-surface-700">{countLabel(key)}</span>
                      <span className="font-semibold tabular-nums text-danger-700">{n}</span>
                    </li>
                  ))}
                </ul>
              )}
              {impact.total > 0 && (
                <p className="text-xs text-surface-500 mt-2">{t('organization.hardDelete.totalRecords', { count: impact.total })}</p>
              )}
            </div>

            {blocking.length > 0 && (
              <div className="rounded-lg border border-warning-200 bg-warning-50 p-3">
                <p className="text-sm font-semibold text-warning-900 flex items-center gap-2">
                  <Ban size={16} /> {t('organization.hardDelete.blockedTitle')}
                </p>
                <ul className="list-disc ps-5 mt-1 text-sm text-warning-900">
                  {blocking.map((b) => <li key={b}>{t(`organization.deleteBlocking.${b}`, b)}</li>)}
                </ul>
              </div>
            )}

            {blocking.length === 0 && (
              <>
                <div>
                  <label className="label" htmlFor="hard-delete-reason">{t('organization.hardDelete.reason')} *</label>
                  <textarea id="hard-delete-reason" rows={3} className="input" value={reason}
                    onChange={(e) => setReason(e.target.value)} />
                  <p className="text-xs text-surface-500 mt-1">{t('sales.reasonMinLength', { count: MIN_REASON })}</p>
                </div>
                <div>
                  <label className="label" htmlFor="hard-delete-name">
                    {t('organization.hardDelete.typeName')} <span className="font-semibold text-surface-900">{expectedName}</span>
                  </label>
                  <input id="hard-delete-name" className="input" value={confirmName} autoComplete="off"
                    onChange={(e) => setConfirmName(e.target.value)} />
                  {confirmName.trim() && !nameMatches && (
                    <p className="text-xs text-danger-700 mt-1">{t('organization.hardDelete.nameMismatch')}</p>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {deleteMutation.isError && (
          <InlineError>{apiErrorMessage(deleteMutation.error, t('organization.hardDelete.failed'))}</InlineError>
        )}

        <div className="flex gap-2 justify-end pt-1">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={deleteMutation.isPending}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-danger" disabled={!canDelete} onClick={() => confirmOnce(() => deleteMutation.mutate())}>
            <Trash2 size={16} />
            {deleteMutation.isPending ? t('common.deleting') : t('organization.hardDelete.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
